import { useState } from 'react';
import { FaChevronLeft, FaChevronRight, FaQuoteLeft, FaStar } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './../styles/Features.css';

const Features = () => {
  const [showAll, setShowAll] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  const features = [
    {
      icon: "🚀",
      title: "Fast Performance",
      description: "Our app is optimized for speed and efficiency.",
      moreInfo: "Uses advanced caching, code splitting, and optimized assets for peak performance."
    },
    {
      icon: "🎨",
      title: "Beautiful Design",
      description: "Modern UI/UX design that delights users.",
      moreInfo: "Follows Material Design principles with custom theming and animations."
    },
    {
      icon: "🔒",
      title: "Secure",
      description: "Your data is protected with top security measures.",
      moreInfo: "Implements end-to-end encryption and regular security audits."
    },
    {
      icon: "⚡",
      title: "Lightning Fast",
      description: "Optimized for maximum performance with load times under 1 second. Built with React 18 and modern JavaScript.",
      moreInfo: "Uses code splitting, lazy loading, and optimized assets for peak efficiency."
    },
    {
      icon: "🎨",
      title: "Pixel-Perfect Design",
      description: "Crafted by professional designers with attention to every detail.",
      moreInfo: "Follows Material Design 3 principles with custom theming options."
    },
    {
      icon: "🔒",
      title: "Enterprise Security",
      description: "End-to-end encryption and regular security audits to protect your data.",
      moreInfo: "SOC 2 compliant with regular penetration testing and vulnerability scans."
    },
    {
      icon: "🔄",
      title: "Real-Time Updates",
      description: "Live data synchronization across all your devices instantly.",
      moreInfo: "Powered by WebSockets and Firebase for seamless real-time experiences."
    },
    {
      icon: "📱",
      title: "Fully Responsive",
      description: "Looks perfect on any device from mobile to desktop.",
      moreInfo: "Mobile-first design with adaptive breakpoints and touch optimization."
    },
    {
      icon: "🌐",
      title: "Multi-Language",
      description: "Supports 15+ languages with automatic locale detection.",
      moreInfo: "Integrated with i18n for easy translation management."
    },
    {
      icon: "🔍",
      title: "Advanced Analytics",
      description: "Comprehensive insights and reporting tools for data-driven decisions.",
      moreInfo: "Real-time analytics dashboard with custom reporting capabilities."
    },
    {
      icon: "🤝",
      title: "24/7 Support",
      description: "Round-the-clock customer support and technical assistance.",
      moreInfo: "Dedicated support team with average response time under 30 minutes."
    },
    {
      icon: "📊",
      title: "Custom Integrations",
      description: "Seamless integration with your existing tools and workflows.",
      moreInfo: "API-first approach with extensive documentation and SDKs."
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: "Priya Mehta",
      SuccessStory: "eCommerce Website",
      content: "Working with Webcrux Technology on our e-commerce website was a smooth experience from day one. They understood our vision, offered thoughtful suggestions, and built a site that feels just right for our customers. The design is clean, the checkout is easy, and everything just works.",
      rating: 5,
      image: "https://randomuser.me/api/portraits/women/65.jpg"
    },
    {
      id: 1,
      name: "Rahul Desai",
      SuccessStory: "SaaS Platform Development",
      content: "Webcrux Technology helped us bring our SaaS idea to life with the right mix of technical skills and clear communication. They handled both the backend and frontend with care, ensuring the app runs smoothly and looks professional. Their team genuinely felt like a partner in the process.",
      rating: 5,
      image: "https://randomuser.me/api/portraits/men/32.jpg"
    },
    {
      id: 2,
      name: "Aarav Singh",
      SuccessStory: "Internal Business Web App",
      content: "We needed a custom web app to simplify our internal workflow, and Webcrux Technology delivered exactly what we had in mind. The interface is intuitive, the performance is solid, and their team was always ready to tweak things based on our feedback. It’s made our daily work easier.",
      rating: 4,
      image: "https://randomuser.me/api/portraits/men/75.jpg"
    },
    {
      id: 3,
      name: "Neha Patel",
      SuccessStory: "Mobile App for Service-Based Business",
      content: "Webcrux Technology built a mobile app for our service-based platform, and it looks great on both Android and iOS. They paid close attention to design and user flow, making the app feel simple and easy to navigate. We appreciated their regular updates and support.",
      rating: 5,
      image: "https://randomuser.me/api/portraits/women/68.jpg"
    },
    {
      id: 4,
      name: "Siddharth Roy",
      SuccessStory: "Enterprise System Modernization",
      content: "We approached Webcrux to upgrade our old enterprise system to something more modern and maintainable. They helped us restructure the backend, improve the interface, and move to a more secure setup. The transition was smooth, and the platform feels future-ready.",
      rating: 5,
      image: "https://randomuser.me/api/portraits/men/44.jpg"
    }
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <FaStar
        key={i}
        className={i < rating ? "star filled" : "star"}
      />
    ));
  };

  const visibleFeatures = showAll ? features : features.slice(0, 6);

  return (
    <section className="features" id="features">
      <div className="container">
        <div className="section-headers">
          <h2>Why Businesses Trust Webcrux Technology</h2>
          <p className="section-subtitle">We blend innovative technology with intuitive design to create digital solutions your users will love.</p>
        </div>

        <div className="features-grid">
          {visibleFeatures.map((feature, index) => (
            <div className="feature-card" key={index}>
              <div className="feature-icon-container">
                <div className="feature-icon">{feature.icon}</div>
              </div>
              <div className="feature-content">
                <h3>{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                <div className="feature-more">
                  <details>
                    <summary>Learn more</summary>
                    <p>{feature.moreInfo}</p>
                  </details>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="features-cta">
          <button
            className="btn primary"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Show Less' : 'See All Features'}
          </button>

          {/* <a href="/contact" className="btn outline no-underline">
            Request Demo
          </a> */}
          <button
            onClick={() => navigate('/contact')}
            className="btn secondary no-underline"
          >
            Request Demo
          </button>
        </div>
      </div>
      <div className="container">
        <div className="section-header">
          <h2>Success Stories</h2>
          <div className="divider"></div>
          <p className="section-subtitle">Our success is defined by the impact we help our clients achieve</p>
        </div>
      </div>
      <div>
        <div className="testimonial-carousel">
          <button className="nav-button prev" onClick={prevTestimonial}>
            <FaChevronLeft />
          </button>

          <div className="testimonial-card">
            <div className="quote-icon">
              <FaQuoteLeft />
            </div>
            <p className="testimonial-content">
              {testimonials[currentIndex].content}
            </p>
            <div className="testimonial-author">
              <div className="author-image">
                <img
                  src={testimonials[currentIndex].image}
                  alt={testimonials[currentIndex].name}
                />
              </div>
              <div className="author-info" style={{ textAlign: 'start' }}>
                <h4>{testimonials[currentIndex].name}</h4>
                <p className="position">{testimonials[currentIndex].SuccessStory}</p>
                <div className="rating">
                  {renderStars(testimonials[currentIndex].rating)}
                </div>
              </div>
            </div>
          </div>

          <button className="nav-button next" onClick={nextTestimonial}>
            <FaChevronRight />
          </button>
        </div>

        <div className="carousel-dots">
          {testimonials.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </div>
      </div>
    </section>

  );
};

export default Features;